'use client';

import classnames from 'classnames';
import { Fragment } from 'react';
import {
  VideoDetailsPlayer,
  VideoWrapper,
  YouTubeEmbed,
} from '@stadionhq-v3/base-ui';
import { ContentfulImage } from '@stadionhq-v3/contentful-ui';
import { Email, Facebook, Play, X } from '@components/icons';
import { VIDEO_PUBLISH_DATE_FORMAT } from '@core/constants/video.constants';
import { Locale } from '@core/constants/locales.constants';
import {
  getVideoDescription,
  getVideoThumbnailUrl,
  getVideoTitle,
} from '@utils/transforms/video/videoFieldFallbacks';
import Image from 'next/image';
import { Video } from '@graphql/index';
import { CleanContentful } from '@stadionhq-v3/clean-contentful';
import VideoCard, {
  VideoCardProps,
} from '@components/v3/molecules/video-cards';
import RelatedContent from '@components/v3/organisms/related-content';
import styles from '../videoDetailTemplate.module.scss';

export type YouTubeVideoTemplateDictionary = {
  shareLabel: string;
  /** Title for the related-content block below the player ("Up Next"). */
  relatedLabel: string;
  /** Required by the V3 organism's `dictionary` prop even when no carousel. */
  upNextToggleLabel: string;
  previousLabel: string;
  nextLabel: string;
};

type YouTubeVideoTemplateProps = {
  page: CleanContentful<Video> | undefined;
  locale: Locale;
  dictionary: YouTubeVideoTemplateDictionary;
  /**
   * Already-transformed VideoCardProps for the related-content block. The
   * parent decides whether these are "Latest Video" or "Next Up" content
   * (based on the `?playlist=true&playlistSlug=<slug>` URL params).
   */
  relatedVideos?: VideoCardProps[];
};

const YouTubeVideoTemplate = ({
  page,
  locale,
  dictionary,
  relatedVideos,
}: YouTubeVideoTemplateProps) => {
  if (!page) return null;

  const title = getVideoTitle(page);
  const description = getVideoDescription(page);
  const thumbnailUrl = getVideoThumbnailUrl(page);

  const channelTitle = page.video?.channelTitle;

  const playerImage = page.image ? (
    <ContentfulImage {...page.image} />
  ) : thumbnailUrl ? (
    <Image src={thumbnailUrl} alt={title || ''} width={1920} height={1080} />
  ) : null;

  const player = (
    <VideoWrapper
      duration={
        page.video?.duration ? { text: page.video.duration } : undefined
      }
      cardIcon={{ icon: <Play /> }}
      image={playerImage}
    >
      {page.video?.provider === 'youtube' && page.video?.id && (
        <YouTubeEmbed videoId={page.video.id} />
      )}
    </VideoWrapper>
  );

  // V3 RelatedContent iterates over `relatedArticles` and substitutes the
  // matching `altRelatedArticles[index]` ReactNode in each <li>. Lengths must
  // match — supply a placeholder ArticleCardProps shell carrying just the id
  // so the component can use it as the React key.
  const relatedPlaceholders = (relatedVideos ?? []).map((cardProps) => ({
    id: cardProps.slug,
  }));
  const relatedCards = (relatedVideos ?? []).map((cardProps) => (
    <Fragment key={cardProps.slug}>
      <VideoCard {...cardProps} />
    </Fragment>
  ));

  return (
    <div className={classnames(styles.container)}>
      <VideoDetailsPlayer
        classNames={[classnames(styles.detailsPlayer)]}
        title={title ?? ''}
        summary={description}
        video={player}
        category={
          channelTitle
            ? {
                category: channelTitle,
                slug: '#',
                onDark: true,
                disabled: true,
              }
            : undefined
        }
        publishDate={
          page.video?.date
            ? {
                date: new Date(page.video.date),
                format: VIDEO_PUBLISH_DATE_FORMAT,
                locale,
                onDark: true,
              }
            : undefined
        }
        shareTray={{
          shareLabel: dictionary.shareLabel,
          onDark: true,
          sharePlatforms: [
            { id: 'facebook', icon: <Facebook /> },
            { id: 'x', icon: <X /> },
            { id: 'email', icon: <Email /> },
          ],
        }}
        isLive={false}
        onDark
        dictionary={{
          toggleLabel: dictionary.upNextToggleLabel,
          previousLabel: dictionary.previousLabel,
          nextLabel: dictionary.nextLabel,
        }}
      />

      {relatedCards.length > 0 && (
        <RelatedContent
          classNames={[classnames(styles.relatedContent)]}
          title={dictionary.relatedLabel}
          relatedArticles={relatedPlaceholders}
          altRelatedArticles={relatedCards}
          onDark
        />
      )}
    </div>
  );
};

export default YouTubeVideoTemplate;
